import { FC, useState } from 'react';
import UsersTableFilters from '../UsersTableFilters';
import { useUsers } from '../../context/UserManagementContext';
import UsersTableSearch from '../UsersTableSearch';
import { Button, ButtonSizes, ButtonStyles, Icon, Icons, ListItem, MenuButton } from '@silverfort/sf-components';
import UpdateUsersAndTokensPopup, { UploadCsvType } from '../UpdateUsersAndTokensPopup';
import { DateFormat, formatDate } from '../../../../utils/dates';
import useUserTableToolbarActions from './hooks/useUserTableToolbarActions';

const Loader = ({ loading }: { loading: boolean }) => {
    if (!loading) {
        return null;
    }

    return (
        <span className="animate-spin">
            <Icon size={16} icon={Icons.Loader} className="text-sf-gray-500" />
        </span>
    );
};

const UsersTableToolbar: FC = () => {
    const { filters, setFilters } = useUsers();
    const {
        generateAndDownloadUsersFile,
        isGenerateAndDownloadUsersFileLoading,
        downloadLatestUsersUploadReport,
        isDownloadingLatestUserReport,
        downloadLatestToken2UploadReport,
        isDownloadingLatestToken2Report,
        uploadCsv,
        generateAndDownloadToken2TemplateFile,
        latestUploadReport
    } = useUserTableToolbarActions(filters);

    const [popupOpen, setPopupOpen] = useState(false);

    const handleSearchChange = (search: string) => {
        setFilters(({ name, ...prevFilters }) => {
            if (search) {
                return {
                    ...prevFilters,
                    name: search
                };
            }

            return prevFilters;
        });
    };

    const handleUploadCsvFile = (file: FormData, type: UploadCsvType) => {
        uploadCsv(file, type);
    };

    return (
        <div className="flex justify-between">
            <UsersTableFilters />
            <div className="flex flex-row gap-2">
                <UsersTableSearch onSearchChange={handleSearchChange} />
                <Button
                    iconRight={Icons.Update}
                    buttonStyle={ButtonStyles.Tertiary}
                    onClick={() => setPopupOpen(true)}
                />

                <MenuButton
                    buttonSize={ButtonSizes.Medium}
                    buttonStyle={ButtonStyles.TertiaryBorder}
                    className="left-[-180px]"
                    icon={Icons.Download}
                    iconClassName="flex"
                    openByDefault={false}
                    buttonTooltipProps={{ title: 'Download' }}
                    isLoading={
                        isGenerateAndDownloadUsersFileLoading ||
                        isDownloadingLatestUserReport ||
                        isDownloadingLatestToken2Report
                    }
                    content={
                        <div className="max-h-[232px] w-50">
                            <ListItem
                                disabled={isGenerateAndDownloadUsersFileLoading}
                                IconLeft={() => <Loader loading={isGenerateAndDownloadUsersFileLoading} />}
                                onClick={generateAndDownloadUsersFile}
                                title="User CSV"
                            />
                            <ListItem
                                disabled={
                                    !latestUploadReport?.latestProtectedUsersUploadFailureReportId ||
                                    isDownloadingLatestUserReport
                                }
                                onClick={() => {
                                    downloadLatestUsersUploadReport(
                                        latestUploadReport?.latestProtectedUsersUploadFailureReportId
                                    );
                                }}
                                IconLeft={() => <Loader loading={isDownloadingLatestUserReport} />}
                                subtitle={
                                    latestUploadReport?.latestProtectedUsersUploadFailureTimestamp
                                        ? formatDate(
                                              new Date(latestUploadReport?.latestProtectedUsersUploadFailureTimestamp),
                                              DateFormat.ISO_DATETIME
                                          )
                                        : ''
                                }
                                title="Latest user report"
                            />
                            <ListItem
                                disabled={
                                    !latestUploadReport?.latestToken2UploadFailureReportId ||
                                    isDownloadingLatestToken2Report
                                }
                                onClick={() => {
                                    downloadLatestToken2UploadReport(
                                        latestUploadReport?.latestToken2UploadFailureReportId
                                    );
                                }}
                                IconLeft={() => <Loader loading={isDownloadingLatestToken2Report} />}
                                subtitle={
                                    latestUploadReport?.latestToken2UploadFailureReportTimestamp
                                        ? formatDate(
                                              new Date(latestUploadReport?.latestToken2UploadFailureReportTimestamp),
                                              DateFormat.ISO_DATETIME
                                          )
                                        : ''
                                }
                                title="Latest token2 report"
                            />
                        </div>
                    }
                />

                <UpdateUsersAndTokensPopup
                    open={popupOpen}
                    onClose={() => setPopupOpen(false)}
                    onDownloadToken2Template={generateAndDownloadToken2TemplateFile}
                    onUploadCsvFile={handleUploadCsvFile}
                />
            </div>
        </div>
    );
};

export default UsersTableToolbar;
